import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const VehicleForm = ({ vehicle, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    name: '',
    vehicleType: 'Sedan',
    brand: '',
    model: '',
    year: new Date()?.getFullYear(),
    licensePlate: '',
    seats: 5,
    transmission: 'Automatic',
    fuelType: 'Essence',
    dailyRate: 0,
    dailyRateUsd: 0,
    status: 'available',
    image: '',
    imageAlt: '',
    description: '',
    features: [],
    mileage: 0
  });
  const [featureInput, setFeatureInput] = useState('');

  useEffect(() => {
    if (vehicle) {
      setFormData({
        name: vehicle?.name || '',
        vehicleType: vehicle?.vehicleType || 'Sedan',
        brand: vehicle?.brand || '',
        model: vehicle?.model || '',
        year: vehicle?.year || new Date()?.getFullYear(),
        licensePlate: vehicle?.licensePlate || '',
        seats: vehicle?.seats || 5,
        transmission: vehicle?.transmission || 'Automatic',
        fuelType: vehicle?.fuelType || 'Essence',
        dailyRate: vehicle?.dailyRate || 0,
        dailyRateUsd: vehicle?.dailyRateUsd || 0,
        status: vehicle?.status || 'available',
        image: vehicle?.image || '',
        imageAlt: vehicle?.imageAlt || '',
        description: vehicle?.description || '',
        features: vehicle?.features || [],
        mileage: vehicle?.mileage || 0
      });
    }
  }, [vehicle]);

  const handleSubmit = (e) => {
    e?.preventDefault();
    onSubmit(formData);
  };

  const addFeature = () => {
    if (featureInput?.trim()) {
      setFormData(prev => ({ ...prev, features: [...prev?.features, featureInput?.trim()] }));
      setFeatureInput('');
    }
  };

  const removeFeature = (index) => {
    setFormData(prev => ({ ...prev, features: prev?.features?.filter((_, i) => i !== index) }));
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-xl shadow-premium border border-border max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border p-4 md:p-6 flex items-center justify-between">
          <h2 className="text-xl md:text-2xl font-bold text-foreground">
            {vehicle ? 'Modifier le Véhicule' : 'Ajouter un Véhicule'}
          </h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <Icon name="X" className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 md:p-6 space-y-4 md:space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Nom du véhicule"
              value={formData?.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e?.target?.value }))}
              required
            />
            <Select
              label="Type de véhicule"
              value={formData?.vehicleType}
              onChange={(e) => setFormData(prev => ({ ...prev, vehicleType: e?.target?.value }))}
              options={[
                { value: 'Sedan', label: 'Sedan' },
                { value: 'SUV', label: 'SUV' },
                { value: 'Van', label: 'Van' },
                { value: 'Truck', label: 'Truck' },
                { value: 'Luxury', label: 'Luxury' },
                { value: 'Bus', label: 'Bus' }
              ]}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              label="Marque"
              value={formData?.brand}
              onChange={(e) => setFormData(prev => ({ ...prev, brand: e?.target?.value }))}
              required
            />
            <Input
              label="Modèle"
              value={formData?.model}
              onChange={(e) => setFormData(prev => ({ ...prev, model: e?.target?.value }))}
              required
            />
            <Input
              label="Année"
              type="number"
              value={formData?.year}
              onChange={(e) => setFormData(prev => ({ ...prev, year: parseInt(e?.target?.value) || new Date()?.getFullYear() }))}
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Plaque d'immatriculation"
              value={formData?.licensePlate}
              onChange={(e) => setFormData(prev => ({ ...prev, licensePlate: e?.target?.value }))}
              required
            />
            <Input
              label="Nombre de sièges"
              type="number"
              value={formData?.seats}
              onChange={(e) => setFormData(prev => ({ ...prev, seats: parseInt(e?.target?.value) || 5 }))}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Transmission"
              value={formData?.transmission}
              onChange={(e) => setFormData(prev => ({ ...prev, transmission: e?.target?.value }))}
              options={[
                { value: 'Manual', label: 'Manuelle' },
                { value: 'Automatic', label: 'Automatique' }
              ]}
            />
            <Select
              label="Type de carburant"
              value={formData?.fuelType}
              onChange={(e) => setFormData(prev => ({ ...prev, fuelType: e?.target?.value }))}
              options={[
                { value: 'Essence', label: 'Essence' },
                { value: 'Diesel', label: 'Diesel' },
                { value: 'Électrique', label: 'Électrique' },
                { value: 'Hybride', label: 'Hybride' }
              ]}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Tarif journalier (CDF)"
              type="number"
              value={formData?.dailyRate}
              onChange={(e) => setFormData(prev => ({ ...prev, dailyRate: parseFloat(e?.target?.value) || 0 }))}
              required
            />
            <Input
              label="Tarif journalier (USD)"
              type="number"
              value={formData?.dailyRateUsd}
              onChange={(e) => setFormData(prev => ({ ...prev, dailyRateUsd: parseFloat(e?.target?.value) || 0 }))}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Statut"
              value={formData?.status}
              onChange={(e) => setFormData(prev => ({ ...prev, status: e?.target?.value }))}
              options={[
                { value: 'available', label: 'Disponible' },
                { value: 'rented', label: 'Loué' },
                { value: 'maintenance', label: 'Maintenance' },
                { value: 'reserved', label: 'Réservé' }
              ]}
            />
            <Input
              label="Kilométrage"
              type="number"
              value={formData?.mileage}
              onChange={(e) => setFormData(prev => ({ ...prev, mileage: parseInt(e?.target?.value) || 0 }))}
            />
          </div>

          <Input
            label="URL de l'image"
            value={formData?.image}
            onChange={(e) => setFormData(prev => ({ ...prev, image: e?.target?.value }))}
            placeholder="https://..."
          />

          <Input
            label="Texte alternatif de l'image"
            value={formData?.imageAlt}
            onChange={(e) => setFormData(prev => ({ ...prev, imageAlt: e?.target?.value }))}
            placeholder="Description de l'image"
          />

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Description</label>
            <textarea
              value={formData?.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e?.target?.value }))}
              className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              rows="3"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Caractéristiques</label>
            <div className="flex gap-2 mb-2">
              <Input
                value={featureInput}
                onChange={(e) => setFeatureInput(e?.target?.value)}
                placeholder="Ajouter une caractéristique"
                onKeyPress={(e) => e?.key === 'Enter' && (e?.preventDefault(), addFeature())}
              />
              <Button type="button" onClick={addFeature} iconName="Plus" />
            </div>
            <div className="space-y-2">
              {formData?.features?.map((feature, index) => (
                <div key={index} className="flex items-center gap-2 bg-muted p-2 rounded-lg">
                  <span className="flex-1 text-foreground">{feature}</span>
                  <button type="button" onClick={() => removeFeature(index)} className="text-red-500 hover:text-red-600">
                    <Icon name="X" className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row justify-end gap-3 pt-4 border-t border-border">
            <Button type="button" variant="outline" onClick={onClose} className="w-full sm:w-auto">
              Annuler
            </Button>
            <Button type="submit" iconName="Save" iconPosition="left" className="w-full sm:w-auto">
              {vehicle ? 'Modifier' : 'Créer'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default VehicleForm;